import { useForm } from '@abyss/web/hooks/useForm';
import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';
import { Accordion } from '@abyss/web/ui/Accordion';
import { Button } from '@abyss/web/ui/Button';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { Flex } from '@abyss/web/ui/Flex';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { Heading } from '@abyss/web/ui/Heading';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Link } from '@abyss/web/ui/Link';
import { RadioGroup } from '@abyss/web/ui/RadioGroup';
import { Text } from '@abyss/web/ui/Text';
import { TextInput } from '@abyss/web/ui/TextInput';
import { t } from 'i18next';
import find from 'lodash/find';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../../common/Constants';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { useConfig } from '../../../frontends/ProviderSearch/context/Analytics';
import { getLanguage } from '../../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useLagoon } from '../../../hooks/useLagoon';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { removeSpecialCharactersUsingMatch } from '../../../utils/smartChoice.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { phoneOnly } from '../../ConstantsStyles';
import { ContentWrapper } from '../../ContentWrapper';
import { ClearInputButton } from './ClearInputButton';
import { CtaConfirmationModal } from './CtaConfirmationModal';
import { InfoDrawerOrPopover } from './InfoDrawerOrPopover';
import { properties } from './OnboardingForm';
import {
  ClearButtonWrapper,
  HeadingText,
  InputLikeAccordionContainer,
  NewStyledDrawer,
  SearchInputWrapper,
  StyledCheckBoxGroup,
  StyledDivider,
  StyledMarkdown,
} from './SmartChoice.styled';
import { determineIsSuperUser } from './superUserUtils';
import {
  countDifferences,
  formatPreferenceData,
  getLanguagesSpokenValues,
  getSelectedGender,
  getSelectedLanguage,
} from './utils';

export interface RecommendationProp {
  createdDate?: string;
  distance?: string;
  evngApptAvbl?: string;
  hasDismissedCoachMark?: string;
  hasDismissedCoachMarkWeb?: string;
  hasSetPreferences?: string;
  lastUpdatedDate?: string;
  onlineApptSchdlAvbl?: string;
  providerBoardCertStatus?: string;
  providerGender?: string;
  uuid?: string;
  virtualApptAvbl?: string;
  wkndApptAvbl?: string;
  languagesSpoken?: string[];
  hasDefaultPreferences?: string;
  memberRandomizedKey?: string;
}
export interface Props {
  isOpen: boolean;
  handleCloseDrawer: () => void;
  onClickSaveAndUpdate: ({}) => void;
  preferencesData: RecommendationProp;
  preferencesFormFromProviderDetail: boolean;
  isSuperUser?: boolean;
  onUnsavedChanges?: (hasChanges: boolean) => void;
}

export const getUserSelections = async (form, currentSelectedLanguage) => {
  let userSelections = '';
  if (form?.distance) {
    userSelections += `Distance with in: ${form?.distance} miles | `;
  }
  if (form?.virtualApptAvbl) {
    userSelections += `Virtual appointments | `;
  }
  if (form?.onlineApptSchdlAvbl) {
    userSelections += `Online appointment scheduling | `;
  }
  if (form?.evngApptAvbl) {
    userSelections += `Evening appointments | `;
  }
  if (form?.wkndApptAvbl) {
    userSelections += `Weekend appointments | `;
  }
  if (form?.languagesSpoken?.length) {
    const selectedLanguages = await getSelectedLanguage(
      form?.languagesSpoken,
      currentSelectedLanguage
    );
    userSelections += `Languages spoken: ${selectedLanguages
      ?.map((item) => removeSpecialCharactersUsingMatch(item ?? ''))
      ?.join(';')} | `;
  }
  if (form?.providerGender?.length) {
    userSelections += `Gender: ${getSelectedGender(
      form?.providerGender,
      genderList
    )?.join(';')} | `;
  }
  if (form?.providerBoardCertStatus) {
    userSelections += `Board certified: on | `;
  }
  if (userSelections?.endsWith(' | ')) {
    userSelections = userSelections?.slice(0, -2);
  }
  return userSelections;
};

const distanceMiles = ['1', '5', '10', '25', '50', '100', '120'];
const genderList = [
  { value: 'M', title: 'Male' },
  { value: 'F', title: 'Female' },
];

export const PreferencesFormV2 = ({
  isOpen,
  handleCloseDrawer,
  preferencesData,
  onClickSaveAndUpdate,
  preferencesFormFromProviderDetail,
  isSuperUser: isSuperUserProp,
  onUnsavedChanges,
}: Props) => {
  const mobileScreen = useMediaQuery(phoneOnly);

  const [disabledSubmit, setDisabledSubmit] = useState(0);
  const [languagesSpoken, setLanguagesSpoken] = useState<
    { title: string; value: string }[]
  >([]);
  const [languageSearchQuery, setLanguageSearchQuery] = useState('');
  const [openAccordion, setOpenAccordion] = useState<string>('');
  const distanceAccordionRef = useRef<HTMLDivElement>(null);
  const languageAccordionRef = useRef<HTMLDivElement>(null);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Close accordion when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openAccordion === 'distance' &&
        distanceAccordionRef.current &&
        !distanceAccordionRef.current.contains(event.target as Node)
      ) {
        setOpenAccordion('');
      }
      if (
        openAccordion === 'language' &&
        languageAccordionRef.current &&
        !languageAccordionRef.current.contains(event.target as Node)
      ) {
        setOpenAccordion('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openAccordion]);

  const lagoonData = useLagoon('ui-messaging')();
  const allConfigs = useLagoon('config');
  const isWidget = useStore(StoreKeys?.IS_WIDGET);
  const { portalData } = useContext(PortalContext);
  const isUmr = isUmrMember(portalData?.portalName);
  const baseUrl = isUmr ? config('UMR_BASE_URL') : config('BASE_URL');

  const isSuperUserValue = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPER_USER
  );
  const isSuperUser =
    isSuperUserProp ?? determineIsSuperUser(allConfigs, isSuperUserValue);

  const IMPROVED_PREFERENCES_FORM_HEADING_DESCRIPTION = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .IMPROVED_PREFERENCES_FORM_HEADING_DESCRIPTION,
  });
  const IMPROVED_PREFERENCES_FORM_FAQ_LINK_TEXT = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .IMPROVED_PREFERENCES_FORM_FAQ_LINK_TEXT,
  });
  const PREFERENCES_FORM_CONVENIENCE_TITLE_DESC = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_CONVENIENCE_TITLE_DESC,
  });
  const IMPROVED_PREFERENCES_FORM_DISTANCE_WITHIN = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .IMPROVED_PREFERENCES_FORM_DISTANCE_WITHIN,
  });
  const IMPROVED_PREFERENCES_FORM_DISTANCE_PLACEHOLDER = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .IMPROVED_PREFERENCES_FORM_DISTANCE_PLACEHOLDER,
  });
  const IMPROVED_PREFERENCES_FORM_LANGUAGE_PLACEHOLDER = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .IMPROVED_PREFERENCES_FORM_LANGUAGE_PLACEHOLDER,
  });
  const IMPROVED_PREFERENCES_FORM_CONVENIENCE_INFO = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .IMPROVED_PREFERENCES_FORM_CONVENIENCE_INFO,
  });
  const IMPROVED_PREFERENCES_FORM_PERSONAL_FIT_INFO = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .IMPROVED_PREFERENCES_FORM_PERSONAL_FIT_INFO,
  });
  const IMPROVED_PREFERENCES_FORM_CANCEL_BUTTON = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .IMPROVED_PREFERENCES_FORM_CANCEL_BUTTON,
  });
  const IMPROVED_PREFERENCES_FORM_WITHIN = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .IMPROVED_PREFERENCES_FORM_WITHIN,
  });
  const IMPROVED_PREFERENCES_FORM_SEARCH_LANGUAGES_PLACEHOLDER = find(
    lagoonData,
    {
      key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
        .IMPROVED_PREFERENCES_FORM_SEARCH_LANGUAGES_PLACEHOLDER,
    }
  );
  const IMPROVED_PREFERENCES_FORM_BOARD_CERTIFIED = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .IMPROVED_PREFERENCES_FORM_BOARD_CERTIFIED,
  });
  const IMPROVED_PREFERENCES_FORM_OTHER_CONVENIENCE_PREFERENCES = find(
    lagoonData,
    {
      key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
        .IMPROVED_PREFERENCES_FORM_OTHER_CONVENIENCE_PREFERENCES,
    }
  );
  const PREFERENCES_FORM_VIRTUAL_APPOINTMENTS = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_VIRTUAL_APPOINTMENTS,
  });
  const PREFERENCES_FORM_ONLINE_APPOINTMENT_SCHEDULING = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_ONLINE_APPOINTMENT_SCHEDULING,
  });
  const PREFERENCES_FORM_EVENING_APPOINTMENTS = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_EVENING_APPOINTMENTS,
  });
  const PREFERENCES_FORM_WEEKEND_APPOINTMENTS = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_WEEKEND_APPOINTMENTS,
  });
  const PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC,
  });
  const PREFERENCES_FORM_LANGUAGES_SPOKEN = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_LANGUAGES_SPOKEN,
  });
  const PREFERENCES_FORM_PROVIDER_GENDER = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_PROVIDER_GENDER,
  });
  const PREFERENCES_FORM_BOARD_CERTIFIED = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_BOARD_CERTIFIED,
  });
  const SAVE_PREFERENCES = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SAVE_PREFERENCES,
  });
  const PREFERENCES_FORM_MILE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_MILE,
  });
  const PREFERENCES_FORM_MILES = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_MILES,
  });
  const PREFERENCES_FORM_UPDATE_PROVIDER_INSIGHTS = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_UPDATE_PROVIDER_INSIGHTS,
  });
  const UPDATE_PROVIDER_INSIGHTS_CONFIRMATION_MODAL = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .UPDATE_PROVIDER_INSIGHTS_CONFIRMATION_MODAL,
  });
  const UPDATE_SEARCH_RESULTS_CONFIRMATION_MODAL = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .UPDATE_SEARCH_RESULTS_CONFIRMATION_MODAL,
  });
  const SAVE_PREFERENCE_CONFIRMATION_MODAL = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SAVE_PREFERENCE_CONFIRMATION_MODAL,
  });
  const UPDATE_PROVIDER_INSIGHTS_CONFIRMATION_MODAL_TITLE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .UPDATE_PROVIDER_INSIGHTS_CONFIRMATION_MODAL_TITLE,
  });
  const UPDATE_SEARCH_RESULTS_CONFIRMATION_MODAL_TITLE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .UPDATE_SEARCH_RESULTS_CONFIRMATION_MODAL_TITLE,
  });
  const SAVE_PREFERENCE_CONFIRMATION_MODAL_TITLE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SAVE_PREFERENCE_CONFIRMATION_MODAL_TITLE,
  });
  const UPDATE_SEARCH_RESULTS = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.UPDATE_SEARCH_RESULTS,
  });

  const SMART_CHOICE_PES_LANG_CODES = useConfig(
    ConstantsLagoon.CONFIG.SMART_CHOICE_PES_LANG_CODES
  );

  const { code: currentSelectedLanguage } = getLanguage();

  const form = useForm({
    defaultValues: {
      distance: null,
      virtualApptAvbl: false,
      onlineApptSchdlAvbl: false,
      evngApptAvbl: false,
      wkndApptAvbl: false,
      // call_support: false,
      languagesSpoken: [],
      providerGender: [],
      providerBoardCertStatus: false,
    },
    values: formatPreferenceData(preferencesData as any),
  });

  const { search } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
    }))
  );

  // This ensures the form always reflects the latest saved preferences
  useEffect(() => {
    if (preferencesData) {
      const formattedData = formatPreferenceData(preferencesData as any);
      form.reset(formattedData);
    }
  }, [preferencesData]);

  // Hide SelectInputMulti language display text
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent =
      '.abyss-select-input-multi-hide-pills-content{display:none!important}';
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  useEffect(() => {
    const fetchLanguages = async () => {
      const LANGUAGES_SPOKEN = await getLanguagesSpokenValues(
        currentSelectedLanguage,
        SMART_CHOICE_PES_LANG_CODES
      );
      setLanguagesSpoken(LANGUAGES_SPOKEN);
    };

    fetchLanguages();
  }, [currentSelectedLanguage, SMART_CHOICE_PES_LANG_CODES]);

  // Use form.watch() to properly subscribe to form value changes
  const formValues = form.watch();

  // Monitor form changes
  useEffect(() => {
    if (formValues) {
      const formFields = { ...formValues };
      const preferencesDataFields = formatPreferenceData(
        preferencesData as any
      );

      const difference = countDifferences(formFields, preferencesDataFields);
      setDisabledSubmit(difference);

      if (onUnsavedChanges) {
        onUnsavedChanges(difference > 0);
      }
    }
  }, [formValues, preferencesData, onUnsavedChanges]);

  const languageDisplayText = useMemo(() => {
    const selectedLanguages = formValues.languagesSpoken;
    if (!selectedLanguages?.length) return '';

    const selectedTitles = selectedLanguages.map((value) => {
      const foundLanguage = languagesSpoken.find(
        (lang) => lang.value === value
      );
      return foundLanguage ? foundLanguage.title : value;
    });

    const count = selectedLanguages.length;
    let formattedLanguages = `(${count}) ${selectedTitles.join(', ')}`;

    const maxLength = mobileScreen ? 35 : 40;

    if (formattedLanguages.length > maxLength) {
      const truncateLength = mobileScreen ? 35 : 50;
      formattedLanguages = formattedLanguages.slice(0, truncateLength) + '...';
    }

    return formattedLanguages;
  }, [formValues.languagesSpoken, languagesSpoken, mobileScreen]);

  const filteredLanguages = useMemo(() => {
    if (!languageSearchQuery) return languagesSpoken;
    return languagesSpoken.filter((lang) =>
      lang.title.toLowerCase().includes(languageSearchQuery.toLowerCase())
    );
  }, [languagesSpoken, languageSearchQuery]);

  const getButtonTitle = () => {
    if (preferencesFormFromProviderDetail) {
      return {
        buttonTitle: PREFERENCES_FORM_UPDATE_PROVIDER_INSIGHTS?.title,
        saveConfirmation: UPDATE_PROVIDER_INSIGHTS_CONFIRMATION_MODAL?.message,
        cancelConfirmation: UPDATE_PROVIDER_INSIGHTS_CONFIRMATION_MODAL?.title,
        confirmationTitle:
          UPDATE_PROVIDER_INSIGHTS_CONFIRMATION_MODAL_TITLE?.title,
      };
      // Update provider insights
    }
    if (search) {
      return {
        buttonTitle: UPDATE_SEARCH_RESULTS?.title,
        saveConfirmation: UPDATE_SEARCH_RESULTS_CONFIRMATION_MODAL?.message,
        cancelConfirmation: UPDATE_SEARCH_RESULTS_CONFIRMATION_MODAL?.title,
        confirmationTitle:
          UPDATE_SEARCH_RESULTS_CONFIRMATION_MODAL_TITLE?.title,
      };
      // Update search results
    }
    return {
      buttonTitle: SAVE_PREFERENCES?.title,
      saveConfirmation: SAVE_PREFERENCE_CONFIRMATION_MODAL?.message,
      cancelConfirmation: SAVE_PREFERENCE_CONFIRMATION_MODAL?.title,
      confirmationTitle: SAVE_PREFERENCE_CONFIRMATION_MODAL_TITLE?.title,
    };
    // Search preferences
  };

  // Form submit handler
  const onSubmit = async () => {
    const {
      distance,
      virtualApptAvbl,
      onlineApptSchdlAvbl,
      evngApptAvbl,
      wkndApptAvbl,
      languagesSpoken,
      providerGender,
      providerBoardCertStatus,
    } = formValues;

    const payload = {
      uuid: preferencesData?.uuid,
      hasSetPreferences: 'y',
      hasDismissedCoachMark: preferencesData?.hasDismissedCoachMark,
      hasDismissedCoachMarkWeb: 'y',
      distance: distance || '',
      virtualApptAvbl: virtualApptAvbl ? 'y' : 'n',
      onlineApptSchdlAvbl: onlineApptSchdlAvbl ? 'y' : 'n',
      evngApptAvbl: evngApptAvbl ? 'y' : 'n',
      wkndApptAvbl: wkndApptAvbl ? 'y' : 'n',
      providerBoardCertStatus: providerBoardCertStatus ? 'y' : 'n',
      providerGender: providerGender?.length ? providerGender?.join(',') : '',
      languagesSpoken: languagesSpoken?.length
        ? languagesSpoken?.join(',')
        : '',
      isSuperUser,
    };

    onClickSaveAndUpdate(payload);

    const userSelections = await getUserSelections(
      formValues,
      currentSelectedLanguage
    );
    adobeLinkTrackEvent({
      userSelections,
      name: getButtonTitle()?.buttonTitle,
      location: 'Set preferences',
    });
  };

  // Modal and drawer handlers
  const handleCloseWithConfirmation = () => {
    if (disabledSubmit > 0) {
      setShowConfirmationModal(true);
    } else {
      handleCloseDrawer();
      adobeLinkTrackEvent({
        name: 'Close',
        location: 'Set preferences',
      });
    }
  };

  const handleSaveAndClose = () => {
    setShowConfirmationModal(false);
    onSubmit();
    handleCloseDrawer();
  };

  const handleLeaveWithoutSaving = () => {
    setShowConfirmationModal(false);
    handleCloseDrawer();
  };

  const handleModalClose = () => {
    setShowConfirmationModal(false);
  };

  return (
    <React.Fragment>
      <NewStyledDrawer
        data-auto-testid="smart-choice-preferences-drawer"
        data-testid="smart-choice-preferences-drawer"
        footer={
          <Flex
            css={{
              flexDirection: mobileScreen ? 'column' : 'row',
              position: 'sticky',
              bottom: '0',
              gap: mobileScreen ? '12px' : '24px',
              zIndex: 1,
              backgroundColor: '$white',
            }}
          >
            <Button
              css={{ width: 'auto' }}
              data-testid="search-for-provider-button"
              isDisabled={disabledSubmit === 0}
              onClick={onSubmit}
            >
              {getButtonTitle()?.buttonTitle}
            </Button>
            <Button
              css={{
                width: 'auto',
                '&:disabled': {},
              }}
              data-testid="cancel-for-set-preferences-button"
              onClick={handleCloseDrawer}
              type="button"
              variant="outline"
            >
              {IMPROVED_PREFERENCES_FORM_CANCEL_BUTTON?.title}
            </Button>
          </Flex>
        }
        isOpen={isOpen && !showConfirmationModal}
        onClose={handleCloseWithConfirmation}
        position={properties.position}
        size={properties.drawerSize}
        title={
          <Text
            color="$primary"
            css={{
              fontSize: '28px !important',
              fontWeight: 700,
              paddingTop: '0px',
            }}
            data-testid="smart-choice-preferences-heading"
          >
            {IMPROVED_PREFERENCES_FORM_HEADING_DESCRIPTION?.title}
          </Text>
        }
      >
        <ContentWrapper
          style={{
            padding: mobileScreen ? '0px' : '16px',
            '& form': { marginLeft: '-16px', marginRight: '-16px' },
          }}
        >
          <StyledMarkdown
            css={{
              fontSize: '14px',
              fontWeight: 400,
              fontStyle: 'normal',
              marginBottom: '16px',
              leadingTrim: 'none',
              lineHeight: '140%',
              letterSpacing: 'normal',
              color: '$black !important',
            }}
            data-testid="set-preferences-sub-heading-desc"
          >
            {IMPROVED_PREFERENCES_FORM_HEADING_DESCRIPTION?.message}
          </StyledMarkdown>
          <Link
            data-testid="faq-link"
            href={`${baseUrl}/findcare/faq${isWidget ? '?source=cex' : ''}`}
            onClick={() => {
              adobeLinkTrackEvent({
                name: 'Faqs',
                location: 'smart choice faqs',
              });
            }}
          >
            {IMPROVED_PREFERENCES_FORM_FAQ_LINK_TEXT?.message}
          </Link>
          <StyledDivider />
          <Flex>
            <HeadingText
              data-testid="preferences-convenience-heading"
              offset={2}
            >
              <Flex style={{ alignItems: 'center', gap: '8px' }}>
                {PREFERENCES_FORM_CONVENIENCE_TITLE_DESC?.title}
                <InfoDrawerOrPopover
                  content={IMPROVED_PREFERENCES_FORM_CONVENIENCE_INFO?.message}
                  mobileScreen={mobileScreen}
                  title={IMPROVED_PREFERENCES_FORM_CONVENIENCE_INFO?.title}
                />
              </Flex>
            </HeadingText>
            <Text
              css={{
                marginBottom: '16px',
                fontSize: '16px',
              }}
            >
              {PREFERENCES_FORM_CONVENIENCE_TITLE_DESC?.message}
            </Text>
          </Flex>

          <FormProvider
            data-auto-testid="set-preferences-form"
            data-testid="set-preferences-form"
            onSubmit={onSubmit}
            state={form}
            style={{ margin: '0px' }}
          >
            <Flex css={{ gap: '8px', marginBottom: '16px' }} direction="column">
              <Text
                css={{
                  fontSize: '16px',
                  color: '#4B4D4F',
                  marginBottom: '8px',
                }}
                data-testid="preferences-distance-heading"
                fontWeight="bold"
              >
                {IMPROVED_PREFERENCES_FORM_DISTANCE_WITHIN?.title}
              </Text>
              <div ref={distanceAccordionRef}>
                <InputLikeAccordionContainer
                  isCollapsible
                  onValueChange={(value) => setOpenAccordion(value)}
                  value={openAccordion}
                >
                  <Accordion.Item value="distance">
                    <Accordion.Trigger>
                      <Accordion.Header
                        data-testid="preferences-distance"
                        subText={
                          form.watch('distance')
                            ? `${
                                IMPROVED_PREFERENCES_FORM_WITHIN?.title
                              } ${form.watch('distance')} ${
                                form.watch('distance') &&
                                parseInt(form.watch('distance') || '0') > 1
                                  ? PREFERENCES_FORM_MILES?.title
                                  : PREFERENCES_FORM_MILE?.title
                              }`
                            : IMPROVED_PREFERENCES_FORM_DISTANCE_PLACEHOLDER?.message
                        }
                      >
                        <Heading offset={3}>
                          {IMPROVED_PREFERENCES_FORM_DISTANCE_WITHIN?.title}
                        </Heading>
                      </Accordion.Header>
                      {form.watch('distance') && (
                        <ClearButtonWrapper>
                          <ClearInputButton
                            ariaLabel="Clear distance"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              form.setValue('distance', null);
                            }}
                          />
                        </ClearButtonWrapper>
                      )}
                    </Accordion.Trigger>
                    <Accordion.Content>
                      <div
                        style={{
                          border: '1px solid #D1D3D4',
                          borderRadius: '4px',
                          marginTop: '4px',
                        }}
                      >
                        <RadioGroup
                          label=""
                          model="distance"
                          onChange={() => {
                            setOpenAccordion('');
                          }}
                        >
                          {distanceMiles?.map((item) => {
                            const isSelected = form.watch('distance') === item;
                            return (
                              <label
                                key={item}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '100%',
                                  cursor: 'pointer',
                                  padding: '4px 12px',
                                  userSelect: 'none',
                                  background: isSelected ? '#EDF3FB' : '#fff',
                                }}
                              >
                                <RadioGroup.Radio
                                  data-testid={`preferences-distance-miles`}
                                  key={item}
                                  label={`${item} ${
                                    parseInt(item) > 1
                                      ? PREFERENCES_FORM_MILES?.title
                                      : PREFERENCES_FORM_MILE?.title
                                  }`}
                                  value={item}
                                />
                              </label>
                            );
                          })}
                        </RadioGroup>
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                </InputLikeAccordionContainer>
              </div>
            </Flex>

            <Flex css={{ gap: '8px', marginTop: '16px' }} direction="column">
              <Text
                css={{
                  fontSize: '16px',
                  color: '#4B4D4F',
                }}
                fontWeight="bold"
              >
                {IMPROVED_PREFERENCES_FORM_OTHER_CONVENIENCE_PREFERENCES?.title}
              </Text>
              <Flex
                alignItems="flex-start"
                css={{ gap: '16px' }}
                direction="column"
              >
                <Checkbox
                  data-testid="preferences-virtual-appt-checkbox"
                  label={PREFERENCES_FORM_VIRTUAL_APPOINTMENTS?.title as string}
                  model="virtualApptAvbl"
                />
                {!isUmr && (
                  <>
                    <Checkbox
                      data-testid="preferences-online-scheduling-checkbox"
                      label={
                        PREFERENCES_FORM_ONLINE_APPOINTMENT_SCHEDULING?.title as string
                      }
                      model="onlineApptSchdlAvbl"
                    />
                  </>
                )}
                <Checkbox
                  data-testid="preferences-evening-appt-checkbox"
                  label={PREFERENCES_FORM_EVENING_APPOINTMENTS?.title as string}
                  model="evngApptAvbl"
                />
                <Checkbox
                  data-testid="preferences-weekend-appt-checkbox"
                  label={PREFERENCES_FORM_WEEKEND_APPOINTMENTS?.title as string}
                  model="wkndApptAvbl"
                />
              </Flex>
            </Flex>

            <StyledDivider />
            <Flex>
              <HeadingText
                data-testid="preferences-convenience-heading"
                offset={2}
              >
                <Flex style={{ alignItems: 'center', gap: '8px' }}>
                  {PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC?.title}
                  <InfoDrawerOrPopover
                    content={
                      IMPROVED_PREFERENCES_FORM_PERSONAL_FIT_INFO?.message
                    }
                    mobileScreen={mobileScreen}
                    title={IMPROVED_PREFERENCES_FORM_PERSONAL_FIT_INFO?.title}
                  />
                </Flex>
              </HeadingText>
              <Text
                css={{
                  marginBottom: '16px',
                  fontSize: '16px',
                }}
              >
                {PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC?.message}
              </Text>
            </Flex>

            <Flex css={{ gap: '8px', marginBottom: '16px' }} direction="column">
              <Text
                css={{
                  fontSize: '16px',
                  color: '#4B4D4F',
                  marginBottom: '8px',
                }}
                data-testid="preferences-language-heading"
                fontWeight="bold"
              >
                {PREFERENCES_FORM_LANGUAGES_SPOKEN?.title}
              </Text>
              <div ref={languageAccordionRef}>
                <InputLikeAccordionContainer
                  isCollapsible
                  onValueChange={(value) => setOpenAccordion(value)}
                  value={openAccordion}
                >
                  <Accordion.Item value="language">
                    <Accordion.Trigger>
                      <Accordion.Header
                        data-testid="preferences-languages-spoken"
                        subText={
                          form.watch('languagesSpoken')?.length
                            ? languageDisplayText
                            : IMPROVED_PREFERENCES_FORM_LANGUAGE_PLACEHOLDER?.message
                        }
                      >
                        <Heading offset={3}>
                          {PREFERENCES_FORM_LANGUAGES_SPOKEN?.title}
                        </Heading>
                      </Accordion.Header>
                      {form.watch('languagesSpoken')?.length > 0 && (
                        <ClearButtonWrapper>
                          <ClearInputButton
                            ariaLabel="Clear languages"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              form.setValue('languagesSpoken', []);
                            }}
                          />
                        </ClearButtonWrapper>
                      )}
                    </Accordion.Trigger>
                    <Accordion.Content
                      style={{
                        marginTop: '4px',
                        border: '1px solid #D1D3D4',
                        borderRadius: '4px',
                      }}
                    >
                      <SearchInputWrapper>
                        <TextInput
                          data-testid="language-search-input"
                          inputLeftElement={
                            <IconSymbol
                              color="#323334"
                              icon="search"
                              size={20}
                            />
                          }
                          label=""
                          onChange={(e) =>
                            setLanguageSearchQuery(e.target.value)
                          }
                          placeholder={
                            IMPROVED_PREFERENCES_FORM_SEARCH_LANGUAGES_PLACEHOLDER?.title
                          }
                          value={languageSearchQuery}
                        />
                      </SearchInputWrapper>
                      <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                        {filteredLanguages?.map((item) => {
                          const isSelected = form
                            .watch('languagesSpoken')
                            ?.includes(item?.value);
                          return (
                            <label
                              key={item?.value}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                cursor: 'pointer',
                                padding: '4px 12px',
                                userSelect: 'none',
                                background: isSelected ? '#EDF3FB' : '#fff',
                              }}
                            >
                              <Checkbox
                                data-testid="preferences-language-checkbox"
                                isChecked={isSelected}
                                onChange={() => {
                                  const currentLanguages =
                                    form.watch('languagesSpoken') || [];
                                  if (isSelected) {
                                    form.setValue(
                                      'languagesSpoken',
                                      currentLanguages.filter(
                                        (lang) => lang !== item?.value
                                      )
                                    );
                                  } else {
                                    form.setValue('languagesSpoken', [
                                      ...currentLanguages,
                                      item?.value,
                                    ]);
                                  }
                                }}
                              />
                              <span
                                style={{
                                  color: isSelected ? '#0078D4' : '#222',
                                  fontWeight: isSelected ? 600 : 400,
                                }}
                              >
                                {item?.title}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                </InputLikeAccordionContainer>
              </div>
            </Flex>

            <Flex css={{ gap: '8px', marginTop: '16px' }} direction="column">
              <Text
                css={{
                  fontSize: '16px',
                  color: '#4B4D4F',
                }}
                data-testid="preferences-gender-heading"
                fontWeight="bold"
              >
                {PREFERENCES_FORM_PROVIDER_GENDER?.title}
              </Text>
              <StyledCheckBoxGroup label="" model="providerGender">
                {genderList.map((item) => (
                  <Checkbox
                    data-testid="preferences-gender-checkbox"
                    key={item?.value}
                    label={t(item?.title) as string}
                    value={item?.value}
                  />
                ))}
              </StyledCheckBoxGroup>
            </Flex>

            <Flex css={{ gap: '8px', margin: '16px 0px' }} direction="column">
              <Text
                css={{
                  fontSize: '16px',
                  color: '#4B4D4F',
                }}
                data-testid="preferences-gender-heading"
                fontWeight="bold"
              >
                {IMPROVED_PREFERENCES_FORM_BOARD_CERTIFIED?.title}
              </Text>
              <Checkbox
                data-testid="preferences-board-certified-checkbox"
                label={PREFERENCES_FORM_BOARD_CERTIFIED?.title as string}
                model="providerBoardCertStatus"
              />
            </Flex>
          </FormProvider>
        </ContentWrapper>
      </NewStyledDrawer>
      {showConfirmationModal && (
        <CtaConfirmationModal
          cancelConfirmation={getButtonTitle()?.cancelConfirmation}
          confirmationTitle={getButtonTitle()?.confirmationTitle}
          isOpen={showConfirmationModal}
          onClose={handleModalClose}
          onLeaveWithoutSaving={handleLeaveWithoutSaving}
          onSaveAndClose={handleSaveAndClose}
          saveConfirmation={getButtonTitle()?.saveConfirmation}
        />
      )}
    </React.Fragment>
  );
};
